import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NotificationTemplateResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Save, Check, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import { SendTestNotificationDialog } from './SendTestNotificationDialog';
import { TemplateVariablesHelp } from './TemplateVariablesHelp';
import { useLanguages, getLanguageName } from '@/hooks/useLanguages';

// Froala Editor
import FroalaEditorComponent from 'react-froala-wysiwyg';
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/js/plugins.pkgd.min.js';
import { useAdminUpsertNotificationTemplateContent } from '@/api/auth/admin/admin';
import { getAdminGetNotificationTemplateQueryKey } from '@/api/auth/admin/admin';

interface ContentState {
  subject: string;
  htmlBody: string;
  isDirty: boolean;
}

interface TemplateContentEditorProps {
  template: NotificationTemplateResponse;
}

const FROALA_CONFIG = {
  placeholderText: 'Edit your email template here...',
  charCounterCount: false,
  toolbarButtons: [
    'bold', 'italic', 'underline', 'strikeThrough', '|',
    'fontFamily', 'fontSize', 'color', 'backgroundColor', '|',
    'paragraphFormat', 'align', 'formatOL', 'formatUL', 'outdent', 'indent', '|',
    'insertLink', 'insertImage', 'insertTable', '|',
    'html', 'undo', 'redo'
  ],
  heightMin: 300,
  heightMax: 500,
  key: 'xGE6oF4B3B3B9D9D5F4gKTRe1CD1PGb1DESAb1Kd1EBH1Pd1TKoD6C5G5A4G2C3H3B4B5C6==',
};

/**
 * Editor component for notification template content with language tabs
 */
export function NotificationTemplateContentEditor({ template }: TemplateContentEditorProps) {
  const queryClient = useQueryClient();
  const { languages } = useLanguages();
  const [activeTab, setActiveTab] = useState<string>('en');
  const [contentByLanguage, setContentByLanguage] = useState<Record<string, ContentState>>({});

  // Initialize content state when languages are loaded
  useEffect(() => {
    if (languages.length > 0 && Object.keys(contentByLanguage).length === 0) {
      const initialContent: Record<string, ContentState> = {};
      languages.forEach((lang) => {
        initialContent[lang.code] = { subject: '', htmlBody: '', isDirty: false };
      });
      setContentByLanguage(initialContent);
    }
  }, [languages, contentByLanguage]);

  const { mutate: upsertContent, isPending: isSaving } = useAdminUpsertNotificationTemplateContent({
    mutation: {
      onSuccess: (_, variables) => {
        toast.success(`${getLanguageName(languages, variables.data.language)} content saved`);
        setContentByLanguage((prev) => ({
          ...prev,
          [variables.data.language]: {
            ...prev[variables.data.language],
            isDirty: false,
          },
        }));
        queryClient.invalidateQueries({ queryKey: getAdminGetNotificationTemplateQueryKey(template.id) });
      },
      onError: (error: unknown) => {
        const err = error as { response?: { data?: { detail?: string } }; message?: string };
        logger.error(err.message || 'Unknown error', { context: 'UpsertNotificationTemplateContent', templateId: template.id });
        toast.error('Failed to save content: ' + (err.response?.data?.detail || err.message));
      },
    },
  });

  // Initialize content from template data
  useEffect(() => {
    if (languages.length === 0) return;

    const newContent: Record<string, ContentState> = {};
    languages.forEach((lang) => {
      newContent[lang.code] = { subject: '', htmlBody: '', isDirty: false };
    });

    template.localizations?.forEach((loc) => {
      if (loc.language && loc.language in newContent) {
        newContent[loc.language] = {
          subject: loc.subject || '',
          htmlBody: loc.htmlBody || '',
          isDirty: false,
        };
      }
    });

    setContentByLanguage(newContent);
  }, [template, languages]);

  const handleContentChange = (language: string, field: 'subject' | 'htmlBody', value: string) => {
    setContentByLanguage((prev) => ({
      ...prev,
      [language]: {
        ...prev[language],
        [field]: value,
        isDirty: true,
      },
    }));
  };

  const handleSave = (language: string) => {
    const content = contentByLanguage[language];
    if (!content?.subject.trim() || !content?.htmlBody.trim()) {
      toast.error('Subject and HTML body are required');
      return;
    }

    upsertContent({
      templateId: template.id!,
      data: {
        language: language as 'en' | 'fr' | 'de' | 'es',
        subject: content.subject.trim(),
        htmlBody: content.htmlBody.trim(),
      },
    });
  };

  const existingLanguages = template.localizations?.map((l) => l.language! as string) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Template Content</CardTitle>
        <CardDescription className="flex items-center gap-1">
          Edit email subject and HTML body for each language. Use {'{{ variableName }}'} for template variables.
          <TemplateVariablesHelp />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2">
          <Label htmlFor="language-select">Language</Label>
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger id="language-select" className="w-[240px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages.map((lang) => {
                const hasContent = existingLanguages.includes(lang.code);
                const isDirty = contentByLanguage[lang.code]?.isDirty;
                return (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="flex items-center gap-2">
                      {lang.name}
                      {hasContent && !isDirty && <Check className="h-3 w-3 text-green-600" />}
                      {isDirty && <Circle className="h-3 w-3 fill-yellow-500 text-yellow-500" />}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {(() => {
          const currentLang = languages.find((l) => l.code === activeTab);
          if (!currentLang) return null;

          return (
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={contentByLanguage[activeTab]?.subject || ''}
                  onChange={(e) => handleContentChange(activeTab, 'subject', e.target.value)}
                  placeholder="Welcome to {{productName}}, {{userName}}!"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="htmlBody">HTML Body</Label>
                <FroalaEditorComponent
                  tag="textarea"
                  model={contentByLanguage[activeTab]?.htmlBody || ''}
                  onModelChange={(content: string) => handleContentChange(activeTab, 'htmlBody', content)}
                  config={FROALA_CONFIG}
                />
              </div>
              <div className="flex justify-end gap-2">
                <SendTestNotificationDialog
                  eventName={template.eventName}
                  disabled={!existingLanguages.includes(activeTab)}
                />
                <Button
                  onClick={() => handleSave(activeTab)}
                  disabled={isSaving || !contentByLanguage[activeTab]?.isDirty}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save Content'}
                </Button>
              </div>
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}
